import React, { useEffect, useState } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';
import { langColor, langFromPath } from './codeBlockUtils';
import { Spinner, TrafficLights } from './codeBlockUtils';

import { useTranslation } from 'react-i18next';

function basename(p: string) {
  return p.split('/').filter(Boolean).pop() ?? p;
}

function scopeLabel(p: string) {
  if (p.startsWith('skill://')) return 'skill';
  if (p.startsWith('session://')) return 'session';
  return null;
}

export const DeleteFileBlock: React.FC<{ content: ToolUseBlock }> = ({ content }) => {
  const { t } = useTranslation('pleri/chat');
  const [done, setDone] = useState(false);
  const [isError, setIsError] = useState(false);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (s.conversations[activeConversationId]?.pendingToolUses ?? new Set()).has(content.id)
      : false,
  );
  const resultJson = useToolResultContent(content.id);
  const isRunning = isPending || !resultJson;

  const path = typeof content.input?.path === 'string' ? content.input.path : '…';
  const name = basename(path);
  const lang = langFromPath(name);
  const color = langColor(lang);
  const scope = scopeLabel(path);

  useEffect(() => {
    if (!isRunning && resultJson) {
      const text = (() => { try { return JSON.parse(resultJson) as string; } catch { return resultJson; } })();
      if (typeof text === 'string' && /fail|error|permission|not found/i.test(text)) {
        setIsError(true);
      } else {
        const t = setTimeout(() => setDone(true), 200);
        return () => clearTimeout(t);
      }
    }
  }, [isRunning, resultJson]);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950 shadow-lg">
      <div className="flex items-center gap-2.5 border-b border-gray-800 bg-gray-900/80 px-3 py-1.5">
        <TrafficLights />

        {/* Language dot */}
        <span
          className="h-2 w-2 flex-shrink-0 rounded-full"
          style={{ backgroundColor: color, opacity: done ? 0.35 : 0.85 }}
        />

        {/* Path */}
        <span
          className={`flex-1 truncate font-mono text-xs transition-all duration-500 ${
            done
              ? 'text-gray-600 line-through decoration-red-500/60 decoration-1'
              : 'text-gray-300'
          }`}
          title={path}
        >
          {path}
        </span>

        {/* Scope badge */}
        {scope && (
          <span className="flex-shrink-0 rounded-md bg-gray-800 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wider text-gray-600">
            {scope}
          </span>
        )}

        {/* Status */}
        {isRunning ? (
          <Spinner className="text-yellow-400" />
        ) : isError ? (
          <span className="flex-shrink-0 rounded-md bg-red-900/30 px-1.5 py-0.5 text-xs font-medium text-red-400">
            {t('failed')}
          </span>
        ) : done ? (
          <span className="flex-shrink-0 rounded-md bg-red-900/30 px-1.5 py-0.5 text-xs font-medium text-red-400">
            {t('deleted')}
          </span>
        ) : (
          <div className="h-2 w-2 flex-shrink-0 rounded-full bg-gray-700" />
        )}
      </div>
      {/* Error message */}
      {isError && resultJson && (
        <div className="border-t border-red-900/30 px-3 py-1.5">
          <span className="font-mono text-xs text-red-400">
            {(() => { try { return JSON.parse(resultJson) as string; } catch { return resultJson; } })()}
          </span>
        </div>
      )}
    </div>
  );
};
